import { useEffect, useRef, useState } from 'react'
import confetti from 'canvas-confetti'
import { Trophy as TrophyIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { tierStyle } from './trophyShared'
import type { OriginalTrophy } from '@/api/trophies'

/** 生成完了時のガチャ風リビール演出。レア度で派手さを出し分ける。 */
export default function TrophyRevealOverlay({
  trophy,
  onDone,
}: {
  trophy: OriginalTrophy | null
  onDone: (t: OriginalTrophy) => void
}) {
  const [phase, setPhase] = useState<'build' | 'reveal'>('build')
  const firedRef = useRef(false)

  // 溜め → リビール
  useEffect(() => {
    if (!trophy) return
    // 対象トロフィーが変わったら溜めから再生する意図的な同期更新
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPhase('build')
    firedRef.current = false
    const timer = setTimeout(() => setPhase('reveal'), 650)
    return () => clearTimeout(timer)
  }, [trophy])

  // リビール瞬間にパーティクル
  useEffect(() => {
    if (phase !== 'reveal' || !trophy || firedRef.current) return
    firedRef.current = true
    const { color } = tierStyle(trophy.rarity)
    const legendary = trophy.rarity >= 8
    confetti({
      particleCount: legendary ? 220 : 90,
      spread: legendary ? 120 : 80,
      startVelocity: legendary ? 55 : 40,
      origin: { y: 0.55 },
      colors: legendary ? ['#b06bf0', '#f5b942', '#34d3e0', '#ffffff'] : [color, '#ffffff'],
    })
  }, [phase, trophy])

  if (!trophy) return null
  const { color, glow } = tierStyle(trophy.rarity)
  const revealed = phase === 'reveal'

  return (
    <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-5 bg-black/80 px-6 backdrop-blur-sm">
      <div className="relative flex h-44 w-44 items-center justify-center">
        {/* 溜めのリング */}
        {!revealed && (
          <div
            className="absolute h-28 w-28 animate-ping rounded-full"
            style={{ background: `${color}55` }}
          />
        )}
        {/* オーラ＋トロフィー */}
        <div
          className={`flex h-40 w-40 items-center justify-center rounded-full transition-all duration-700 ${
            revealed ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
          }`}
          style={{ background: `radial-gradient(circle, ${color}55, transparent 70%)`, boxShadow: `0 0 60px ${glow}` }}
        >
          <TrophyIcon
            size={80}
            style={{ color, filter: `drop-shadow(0 0 16px ${glow})` }}
            className={`transition-transform duration-700 ${revealed ? 'scale-100' : 'scale-0'}`}
          />
        </div>
      </div>

      <div className={`text-center transition-opacity duration-700 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
        <p className="text-sm font-medium" style={{ color }}>レア度 {trophy.rarity} / 10</p>
        <p className="mt-1 text-2xl font-bold text-white">{trophy.name}</p>
        {trophy.title && <p className="mt-1 text-sm text-white/70">{trophy.title}</p>}
      </div>

      <Button
        className={`min-h-[44px] transition-opacity duration-700 ${revealed ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => onDone(trophy)}
      >
        詳細を見る
      </Button>
    </div>
  )
}
